import {
  Box,
  FormControl,
  FormHelperText,
  makeStyles,
  OutlinedInput,
  withStyles,
} from '@material-ui/core';
import EmojiPickerIcon from '@src/components/ChatMessageComposer/EmojiPickerIcon';
import React, {
  ChangeEventHandler,
  KeyboardEventHandler,
  RefObject,
  useEffect,
} from 'react';

export const LINE_HEIGHT = 22;
export const MAX_ROWS = 10;
const MIN_LENGTH_TO_CALCULATE_ROWS = 150;
const WIDTH_LETTTER_PIX = 10;
const MAX_ROWS_TO_CALCULATE = 3;
const MAX_LINES_TO_CALCULATE = 2;

export interface IComposerTextInput {
  textInputRef: RefObject<HTMLDivElement | null>;
  totalAllowedCharacters: number;
  placeholder: string;
  helperText: string;
  maxRows: number;
  typeMessageDisabled: boolean;
  handleWrittenMessageChanged:
    | ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
    | undefined;
  handleWrittenMessageKeyDown: KeyboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  >;
  writtenMessage: string;
  maxCharactersExceeded: boolean;
}

const useStyles = makeStyles(() => ({
  extraPadding: {
    '& .MuiOutlinedInput-multiline': {
      paddingTop: '55px !important',
      '&.MuiOutlinedInput-marginDense': {
        paddingTop: '55px !important',
      },
    },
  },
}));

const OutlinedInputStyled = withStyles(theme => ({
  root: {
    color: theme.palette.text.primary,
    fontSize: '15px',
    borderRadius: '20px',
    borderColor: theme.b2chatgray[400],
    padding: '4px 8px 4px 16px',
    lineHeight: '20px',
  },
  adornedEnd: {
    color: 'primary',
  },
}))(OutlinedInput);

const ComposerTextInput: React.FC<IComposerTextInput> = ({
  writtenMessage,
  helperText,
  placeholder,
  maxRows,
  typeMessageDisabled,
  textInputRef,
  maxCharactersExceeded,
  totalAllowedCharacters,
  handleWrittenMessageChanged,
  handleWrittenMessageKeyDown,
}) => {
  const [tempMaxRows, setTempMaxRows] = React.useState(3);

  const showTotalAllowedCharacters =
    totalAllowedCharacters > 0 && totalAllowedCharacters < 5000;

  const remainingCharacters = totalAllowedCharacters - writtenMessage.length;

  const extraHeight = showTotalAllowedCharacters ? 20 : 0;
  const minHeight = LINE_HEIGHT * maxRows + extraHeight;
  const maxHeight = LINE_HEIGHT * MAX_ROWS + extraHeight;

  const { extraPadding } = useStyles();

  useEffect(() => {
    const { length } = writtenMessage;
    const numberOfLines = writtenMessage.split('\n').length;

    if (
      (length > MIN_LENGTH_TO_CALCULATE_ROWS || length === 0) &&
      textInputRef.current
    ) {
      const heigth = textInputRef.current.offsetWidth;

      const calculatedRows = Math.min(
        Math.ceil((WIDTH_LETTTER_PIX * length) / heigth),
        MAX_ROWS_TO_CALCULATE
      );
      setTempMaxRows(calculatedRows);
    } else if (numberOfLines >= MAX_LINES_TO_CALCULATE) {
      setTempMaxRows(MAX_ROWS_TO_CALCULATE);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writtenMessage]);

  return (
    <FormControl fullWidth>
      <OutlinedInputStyled
        id="composer-text-input"
        key="input-message"
        placeholder={placeholder}
        autoFocus
        multiline
        value={writtenMessage}
        fullWidth
        endAdornment={<EmojiPickerIcon textInputRef={textInputRef} />}
        startAdornment={
          showTotalAllowedCharacters &&
          remainingCharacters > 0 && (
            <Box
              position="absolute"
              left="17px"
              top="-2px"
              fontSize={12}
              color="#A1A1AA"
            >
              {remainingCharacters}
            </Box>
          )
        }
        notched={false}
        maxRows={Math.max(maxRows, tempMaxRows)}
        disabled={typeMessageDisabled}
        onChange={
          !typeMessageDisabled ? handleWrittenMessageChanged : undefined
        }
        onKeyDown={
          !typeMessageDisabled ? handleWrittenMessageKeyDown : undefined
        }
        inputRef={textInputRef}
        className={`${showTotalAllowedCharacters ? extraPadding : ''}`}
        style={{ minHeight, maxHeight }}
        data-testid="composer-text-field"
      />

      {maxCharactersExceeded && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default ComposerTextInput;
